(() => {
  /**
   * UG 菜单逻辑
   * - PC 端：左侧标签切换 + 右侧手风琴（鼠标悬停触发）
   * - 移动端：点击切换展开/收起（支持收起当前展开项）
   */

  const isMobile = () => window.innerWidth <= 768;

  // ========== PC 端逻辑 ==========

  // 更新 submenu 高度
  const updateSubmenuHeight = () => {
    if (isMobile()) return;

    const headerMenu = document.querySelector('[header-menu]');
    if (!headerMenu) return;

    // 找到当前激活的菜单项 (aria-expanded="true")
    const activeMenuItem = headerMenu.querySelector('[ref="menuitem"][aria-expanded="true"]');
    if (!activeMenuItem) return;

    // 找到对应的 submenu
    const menuListItem = activeMenuItem.closest('.menu-list__list-item');
    const activeSubmenu = menuListItem?.querySelector('[ref="submenu[]"]');
    if (!activeSubmenu || !(activeSubmenu instanceof HTMLElement)) return;

    // 重新计算高度（需要等待 DOM 更新完成）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const submenuHeight = activeSubmenu.offsetHeight;
        if (headerMenu instanceof HTMLElement) {
          headerMenu.style.setProperty('--submenu-height', `${submenuHeight}px`);
        }
      });
    });
  };

  // 重置菜单内的 tab 状态（移除所有 active 类，让 CSS 默认状态生效）
  const resetMenuTabState = (menuListItem) => {
    if (!menuListItem || isMobile()) return;

    // 重置左侧标签状态
    const tabNames = menuListItem.querySelectorAll('.ug-mega-menu-left-category-name.active');
    const tabContents = menuListItem.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper.active');
    tabNames.forEach(tab => tab.classList.remove('active'));
    tabContents.forEach(content => content.classList.remove('active'));

    // 重置右侧手风琴状态
    const accordionItems = menuListItem.querySelectorAll('.ug-mega-menu-right-item.active');
    accordionItems.forEach(item => item.classList.remove('active'));
  };

  // PC 端：切换左侧标签
  const switchTab = (tab) => {
    if (!tab || isMobile()) return;

    const tabMenuLeft = tab.closest('.ug-mega-menu-left');
    if (!tabMenuLeft) return;

    // 获取当前标签组的所有标签和内容
    const tabNames = tabMenuLeft.querySelectorAll('.ug-mega-menu-left-category-name');
    const tabContents = tabMenuLeft.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper');
    const tabIndex = Array.from(tabNames).indexOf(tab);

    if (tabIndex === -1) return;

    // 移除所有 active 类
    tabNames.forEach(t => t.classList.remove('active'));
    tabContents.forEach(c => c.classList.remove('active'));

    // 添加当前项的 active 类
    tab.classList.add('active');
    tabContents[tabIndex]?.classList.add('active');

    // 更新 submenu 高度
    updateSubmenuHeight();
  };

  // PC 端：切换右侧手风琴
  const toggleAccordion = (item) => {
    if (!item || isMobile()) return;

    const accordionContainer = item.closest('.ug-mega-menu-right');
    if (!accordionContainer) return;

    // 获取所有手风琴项
    const allItems = accordionContainer.querySelectorAll('.ug-mega-menu-right-item');

    // 移除所有 active 类
    allItems.forEach(i => i.classList.remove('active'));

    // 添加当前项的 active 类
    item.classList.add('active');

    // 更新 submenu 高度（手风琴展开/收起会改变高度）
    updateSubmenuHeight();
  };

  // PC 端：监听菜单激活，重置状态
  const initMenuActivationObserver = () => {
    if (isMobile()) return;

    const headerMenu = document.querySelector('[header-menu]');
    if (!headerMenu) return;

    // 监听所有菜单项的 aria-expanded 变化
    const menuItems = headerMenu.querySelectorAll('[ref="menuitem"]');

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-expanded' &&
          mutation.target instanceof Element &&
          mutation.target.getAttribute('aria-expanded') === 'true') {
          // 菜单被激活
          const menuItem = mutation.target;
          const menuListItem = menuItem.closest('.menu-list__list-item');

          // 重置该菜单内的 tab 和手风琴状态
          resetMenuTabState(menuListItem);

          // 更新高度（需要延迟以确保重置生效）
          requestAnimationFrame(() => {
            updateSubmenuHeight();
          });
        }
      });
    });

    // 观察所有菜单项
    menuItems.forEach(item => {
      observer.observe(item, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    });
  };

  // ========== 移动端逻辑 ==========

  // 移动端：切换左侧分类展开/收起（支持收起当前展开项）
  const toggleMobileCategory = (title) => {
    if (!isMobile()) return;

    const wrapper = title.closest('.ug-mega-menu-left-category-list-content-wrapper');
    if (!wrapper) return;

    // 切换当前项的 active 状态
    const isActive = wrapper.classList.contains('active');

    if (isActive) {
      // 如果当前已展开，则收起
      wrapper.classList.remove('active');
    } else {
      // 收起所有其他项
      const allWrappers = wrapper.closest('.ug-mega-menu-left-category-list')
        ?.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper');
      allWrappers?.forEach(item => item.classList.remove('active'));

      // 展开当前项
      wrapper.classList.add('active');
    }
  };

  // 移动端：切换右侧手风琴展开/收起（支持收起当前展开项）
  const toggleMobileAccordion = (title) => {
    if (!isMobile()) return;

    const item = title.closest('.ug-mega-menu-right-item');
    if (!item) return;

    // 切换当前项的 active 状态
    const isActive = item.classList.contains('active');

    if (isActive) {
      // 如果当前已展开，则收起
      item.classList.remove('active');
    } else {
      // 收起所有其他项
      const allItems = item.closest('.ug-mega-menu-right')
        ?.querySelectorAll('.ug-mega-menu-right-item');
      allItems?.forEach(i => i.classList.remove('active'));

      // 展开当前项
      item.classList.add('active');
    }
  };

  // ========== 事件监听 ==========

  const initEventListeners = () => {
    // PC 端：点击切换左侧标签
    document.addEventListener('click', (e) => {
      if (isMobile() || !e.target || !(e.target instanceof Element)) return;
      const clickedTab = e.target.closest('.ug-mega-menu-left-category-name');
      if (clickedTab) switchTab(clickedTab);
    });

    // PC 端：鼠标移入切换左侧标签
    document.addEventListener('mouseenter', (e) => {
      if (isMobile() || !e.target || !(e.target instanceof Element)) return;
      const hoveredTab = e.target.closest('.ug-mega-menu-left-category-name');
      if (hoveredTab) switchTab(hoveredTab);
    }, true);

    // PC 端：鼠标移入展开右侧手风琴
    document.addEventListener('mouseenter', (e) => {
      if (isMobile() || !e.target || !(e.target instanceof Element)) return;
      const hoveredItem = e.target.closest('.ug-mega-menu-right-item');
      if (hoveredItem) toggleAccordion(hoveredItem);
    }, true);

    // 移动端：点击切换左侧分类
    document.addEventListener('click', (e) => {
      if (!isMobile() || !e.target || !(e.target instanceof Element)) return;
      const clickedTitle = e.target.closest('.ug-mega-menu-left-category-list-content-title_mobile');
      if (clickedTitle) {
        e.preventDefault();
        toggleMobileCategory(clickedTitle);
      }
    });

    // 移动端：点击切换右侧手风琴
    document.addEventListener('click', (e) => {
      if (!isMobile() || !e.target || !(e.target instanceof Element)) return;
      const clickedTitle = e.target.closest('.ug-mega-menu-right-item__title');
      if (clickedTitle) {
        e.preventDefault();
        toggleMobileAccordion(clickedTitle);
      }
    });
  };

  // ========== 初始化 ==========

  const init = () => {
    initEventListeners();
    initMenuActivationObserver();
  };

  // DOM 加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();